import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { fillPath, isFreeTemplateCode, useCompanies, useJoinableReports, useReports, useRequestCollaboration, useTemplates, type ReportFilters } from '../api/hooks'
import type { FormInstanceStatus, FormKind, JoinableReportDto } from '../api/types'
import { Badge, Empty, GradeBadge, RevBadge, StatusBadge, fmtAuditPeriod, fmtDate } from '../components/ui'
import DateInput from '../components/DateInput'
import DownloadMenu from '../components/DownloadMenu'
import { useAuth } from '../auth/AuthProvider'
import { problemOf } from '../api/client'

export default function ReportsPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const canEdit = !!user?.roles.some((x) => ['SuperAdmin', 'TenantAdmin', 'Auditor'].includes(x.role))
  // Tenant and super admins already see and edit every report of the company, so there is nothing for them to join.
  const canJoin = canEdit && !user?.roles.some((x) => x.role === 'SuperAdmin' || x.role === 'TenantAdmin')
  const [params, setParams] = useSearchParams()
  const filters: ReportFilters = {
    page: Number(params.get('page') ?? 1),
    pageSize: 25,
    sort: params.get('sort') ?? '-createdAt',
    search: params.get('search') ?? '',
    companyId: params.get('companyId') ?? '',
    templateId: params.get('templateId') ?? '',
    status: (params.get('status') as FormInstanceStatus | null) ?? '',
    kind: (params.get('kind') as FormKind | null) ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    grade: params.get('grade') ?? '',
  }
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    if (key !== 'page') next.delete('page')
    setParams(next)
  }
  const reports = useReports(filters)
  const companies = useCompanies()
  const templates = useTemplates()
  const data = reports.data

  return (
    <div className="stack">
      <div className="row between wrap">
        <h2>{t('reports.list')}</h2>
        <div className="row wrap">
          {canEdit ? <Link className="button secondary" to="/reports/free">{t('nav.freeReport')}</Link> : null}
          <Link className="button" to="/reports/new">{t('nav.newReport')}</Link>
        </div>
      </div>

      {canJoin ? <JoinableReports /> : null}

      <div className="card filters">
        <input placeholder={t('common.search')} value={filters.search} onChange={(e) => set('search', e.target.value)} />
        <select value={filters.companyId} onChange={(e) => set('companyId', e.target.value)}>
          <option value="">{t('common.allCompanies')}</option>
          {companies.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filters.templateId} onChange={(e) => set('templateId', e.target.value)}>
          <option value="">{t('reports.allTemplates')}</option>
          {templates.data?.map((x) => <option key={x.id} value={x.id}>{x.code} — {x.name}</option>)}
        </select>
        <select value={filters.status} onChange={(e) => set('status', e.target.value)}>
          <option value="">{t('reports.allStatuses')}</option>
          {(['Draft', 'Completed', 'Approved', 'Archived'] as const).map((s) => <option key={s} value={s}>{t(`status.${s}`)}</option>)}
        </select>
        <select value={filters.grade} onChange={(e) => set('grade', e.target.value)}>
          <option value="">{t('reports.allGrades')}</option>
          {['A', 'B', 'C', 'D', 'E'].map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <DateInput value={filters.from ?? ''} onChange={(v) => set('from', v)} aria-label={t('reports.from')} />
        <DateInput value={filters.to ?? ''} min={filters.from || undefined} onChange={(v) => set('to', v)} aria-label={t('reports.to')} />
      </div>

      <div className="card">
        {reports.isLoading ? <p>{t('common.loading')}</p> : !data || data.items.length === 0 ? <Empty text={t('reports.empty')} /> : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th><button className="link" onClick={() => set('sort', filters.sort === 'code' ? '-code' : 'code')}>{t('reports.code')}</button></th>
                  <th>{t('reports.title')}</th>
                  <th><button className="link" onClick={() => set('sort', filters.sort === 'company' ? '-company' : 'company')}>{t('companies.title')}</button></th>
                  <th><button className="link" onClick={() => set('sort', filters.sort === 'auditDate' ? '-auditDate' : 'auditDate')}>{t('reports.date')}</button></th>
                  <th>{t('reports.auditors')}</th>
                  <th>{t('reports.status')}</th>
                  <th><button className="link" onClick={() => set('sort', filters.sort === 'percent' ? '-percent' : 'percent')}>{t('reports.result')}</button></th>
                  <th className="num">{t('reports.progress')}</th>
                  <th className="num">{t('reports.findings')}</th>
                  <th aria-label={t('download.title')} />
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <span className="report-code">
                        <Link to={`/reports/${r.id}`}>{r.code ?? r.templateCode}</Link>
                        <RevBadge revisionNo={r.revisionNo} />
                        {r.collaboratorCount > 0 ? <Badge color="#1d6f42" title={t('collab.sharedWith', { count: r.collaboratorCount })}>{t('collab.shared')}</Badge> : null}
                        {r.pendingRequestCount > 0 && r.status === 'Draft' && r.ownerUserId === user?.id ? <span className="count-badge" title={t('collab.pendingCount', { count: r.pendingRequestCount })}>{r.pendingRequestCount}</span> : null}
                      </span>
                    </td>
                    <td>{r.title}<div className="muted small">{r.templateName}</div></td>
                    <td>{r.companyName}{r.siteName ? <div className="muted small">{r.siteName}</div> : null}</td>
                    <td>{fmtAuditPeriod(r.auditStartDate, r.auditStartTime, r.auditEndDate, r.auditEndTime)}</td>
                    <td className="small">{r.auditorNames}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td><GradeBadge grade={r.overallGrade} percent={r.overallPercent} /></td>
                    <td className="num">{isFreeTemplateCode(r.templateCode) ? <span className="muted">–</span> : <>{r.answeredCount}/{r.questionCount}</>}</td>
                    <td className="num">{r.findingCount}{r.majorCount > 0 ? <span className="muted small"> ({r.majorCount} M)</span> : null}</td>
                    <td className="row-actions">
                      {r.status === 'Draft'
                        ? (canEdit ? <Link className="button secondary small-btn" to={fillPath(r)}>{t('reports.continueShort')}</Link> : null)
                        : <DownloadMenu reportId={r.id} variant="menu" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && data.totalPages > 1 ? (
          <div className="row between" style={{ marginTop: 12 }}>
            <span className="muted small">{t('common.pageOf', { page: data.page, pages: data.totalPages, total: data.totalCount })}</span>
            <div className="row">
              <button className="secondary" disabled={data.page <= 1} onClick={() => set('page', String(data.page - 1))}>‹</button>
              <button className="secondary" disabled={data.page >= data.totalPages} onClick={() => set('page', String(data.page + 1))}>›</button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/**
 * "Katılabileceğim raporlar": draft reports of colleagues in the same company. A request goes to the report's owner;
 * once approved the report moves into the normal list. Hidden when there is nothing to join.
 */
function JoinableReports() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const joinable = useJoinableReports()
  const request = useRequestCollaboration()
  const rows = joinable.data ?? []
  // A report leaves this list when the owner approves the request (or finishes it): the main list has changed as well.
  const count = rows.length
  const lastCount = useRef(count)
  useEffect(() => {
    if (lastCount.current !== count) void qc.invalidateQueries({ queryKey: ['reports'] })
    lastCount.current = count
  }, [count, qc])
  if (rows.length === 0) return null

  const action = (row: JoinableReportDto) => {
    const sending = request.isPending && request.variables === row.id
    if (row.myStatus === 'Pending') return <Badge color="#eda100">{t('collab.awaiting')}</Badge>
    return (
      <>
        {row.myStatus === 'Rejected' ? <Badge color="#e34948">{t('collab.rejected')}</Badge> : null}
        <button type="button" className={row.myStatus === 'Rejected' ? 'secondary small-btn' : 'small-btn'} onClick={() => request.mutate(row.id)} disabled={request.isPending}>
          {sending ? t('common.pleaseWait') : row.myStatus === 'Rejected' ? t('collab.requestAgain') : t('collab.request')}
        </button>
      </>
    )
  }

  return (
    <section className="card stack join-card" aria-label={t('collab.joinable')}>
      <div>
        <h3 style={{ margin: 0 }}>{t('collab.joinable')} <span className="muted small">({rows.length})</span></h3>
        <p className="muted small" style={{ margin: '2px 0 0' }}>{t('collab.joinableHint')}</p>
      </div>
      {request.isError ? <p className="error small" role="alert" style={{ margin: 0 }}>{problemOf(request.error).title ?? t('common.error')}</p> : null}
      <div className="join-list">
        {rows.map((row) => (
          <div key={row.id} className="join-row">
            <div className="join-main">
              <div className="row wrap" style={{ gap: 6 }}>
                <Badge>{row.templateCode}</Badge>
                <b className="join-title">{row.title || row.templateName}</b>
              </div>
              <div className="muted small">
                {row.templateName} · {row.companyName} · {t('collab.ownerIs', { name: row.ownerName ?? '–' })} · {fmtDate(row.createdAt)} · {t('collab.itemCount', { count: row.itemCount })}
              </div>
            </div>
            <div className="row wrap join-actions">{action(row)}</div>
          </div>
        ))}
      </div>
    </section>
  )
}
